import { inject, Injectable } from '@angular/core'
import { lastValueFrom } from 'rxjs'
import {
  APIResponse,
  PaginationResponse,
} from '../../modules/core/models/api-validation-result'
import { PaginatedList } from '../../modules/core/models/paginated-list'
import { ApiService } from '../../modules/core/services/api.service'
import { EnvUtilities } from '../../modules/core/utilities/env-utilities'
import { Engineer } from '../../modules/organisation/models/engineer'
import { Notice } from '../../modules/organisation/models/notice'
import { SystemFile } from '../../modules/organisation/models/system-file'
import {
  FileFilters,
  FileIncludes,
  FileOrdering,
} from '../../modules/organisation/services/file.service'
import { HttpService } from '../../modules/rest/services/http.service'
import { PortalToken } from '../../modules/rest/types/portal-token'
import { RestQuery } from '../../modules/rest/utilities/rest-query'
import { Serialisable } from '../../modules/rest/utilities/serialisable'

export interface PortalNoticeFilters {
  unread?: boolean
  keywords?: string
}
export type PortalNoticeOrdering = 'created_at' | '-created_at'

@Injectable({
  providedIn: 'root',
})
export class PortalService extends ApiService {
  protected http = inject(HttpService)

  private currentEngineer: Promise<Engineer> | null = null

  setToken(token: PortalToken) {
    this.http.setPortalToken(token)
  }

  private serviceUrl(path: string) {
    return EnvUtilities.serviceUrl(path, 'engineer')
  }

  async getEngineer(force = false) {
    if (this.http.hasPortalToken()) {
      if (this.currentEngineer == null || force) {
        this.currentEngineer = new Promise<Engineer>((resolve, reject) => {
          this.http
            .get<APIResponse>(this.serviceUrl('auth/me'))
            .then((observable) => {
              lastValueFrom(observable)
                .then((data) => {
                  const engineer = Serialisable.deserialise<Engineer>(
                    data.data,
                    Engineer.serialisationConfig
                  )
                  this.currentEngineer = new Promise<Engineer>((resolve) =>
                    resolve(engineer)
                  )
                  resolve(engineer)
                })
                .catch((error) => {
                  this.http.setPortalToken(null)
                  reject(this.errorResult(error, Engineer.serialisationConfig))
                })
            })
            .catch(() => reject())
        })
      }
    } else {
      return new Promise<Engineer>((_resolve, reject) => reject(null))
    }
    return this.currentEngineer
  }

  async getNotices(
    query: RestQuery<PortalNoticeFilters, PortalNoticeOrdering>
  ) {
    return lastValueFrom(
      await this.http.get<APIResponse<unknown[], PaginationResponse>>(
        this.serviceUrl('notices'),
        query.compile()
      )
    )
      .then(
        (response) =>
          new PaginatedList<Notice>(
            Serialisable.deserialiseList<Notice>(
              response.data ?? [],
              Notice.serialisationConfig
            ),
            response.meta.total,
            response.meta.per_page,
            response.meta.current_page
          )
      )
      .catch((error) => this.error(error))
  }

  async accessNotice(notice: Notice) {
    return lastValueFrom(
      await this.http.post<APIResponse>(this.serviceUrl(`notices/${notice.id}`))
    )
      .then((response) =>
        Serialisable.deserialise<Notice>(
          response.data,
          Notice.serialisationConfig
        )
      )
      .catch((error) => this.error(error, Notice.serialisationConfig))
  }

  async getFiles(query: RestQuery<FileFilters, FileOrdering, FileIncludes>) {
    return lastValueFrom(
      await this.http.get<APIResponse<unknown[], PaginationResponse>>(
        this.serviceUrl('files'),
        query.compile()
      )
    )
      .then((response) => {
        return new PaginatedList<SystemFile>(
          Serialisable.deserialiseList<SystemFile>(
            response.data,
            SystemFile.serialisationConfig
          ),
          response.meta.total,
          response.meta.per_page,
          response.meta.current_page
        )
      })
      .catch((error) => this.error(error))
  }

  async getFile(id: number) {
    return lastValueFrom(
      await this.http.get<APIResponse>(
        this.serviceUrl(`files/file${id < 1 ? '' : `/${id}`}`)
      )
    )
      .then((response) =>
        Serialisable.deserialise<SystemFile>(
          response.data,
          SystemFile.serialisationConfig
        )
      )
      .catch((error) => this.error(error))
  }

  async getFormat(id: number, format: string) {
    return lastValueFrom(
      await this.http.get<{ path: string }>(
        this.serviceUrl(`files/file/${id}/format`),
        {
          format,
        }
      )
    )
      .then((response) => response.path)
      .catch((error) => this.error(error))
  }
}
